// ── MonthPicker ─────────────────────────────────────────────────────────────
// Theme-aware month picker. Replaces native <input type="month">. Same
// value/onChange contract: value is '' or 'YYYY-MM'.
//
// Originally extracted from kostenstellen/ImportWizardPage.PremiumMonthPicker
// so other modules (LieferantenAuswertung, BenefitsTab, etc.) can share the
// same look & feel.

import { useEffect, useLayoutEffect, useRef, useState } from 'react'

const MONTH_NAMES_DE = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
]

export interface MonthPickerProps {
  /** '' for empty, otherwise 'YYYY-MM' */
  value: string
  onChange: (next: string) => void
  ariaLabel?: string
  placeholder?: string
  clearLabel?: string
  /** Optional fixed width (default content-driven) */
  className?: string
  /** Min selectable month, inclusive ('YYYY-MM'). Earlier months are dimmed and unclickable. */
  min?: string
  /** Max selectable month, inclusive ('YYYY-MM'). Later months are dimmed and unclickable. */
  max?: string
  /** 'lg' = taller trigger with larger text, for a page-level period filter. */
  size?: 'default' | 'lg'
}

/** Compare YYYY-MM strings as a 6-digit number (works because both are zero-padded). */
function toNum(ym: string): number {
  const [y, m] = ym.split('-').map(Number)
  return y * 12 + (m - 1)
}

export function MonthPicker({
  value,
  onChange,
  ariaLabel = 'Monat wählen',
  placeholder = 'Monat wählen…',
  clearLabel = 'Filter zurücksetzen',
  className = '',
  min,
  max,
  size = 'default',
}: MonthPickerProps) {
  const minN = min ? toNum(min) : -Infinity
  const maxN = max ? toNum(max) : Infinity
  const [open, setOpen] = useState(false)
  // Right-align the popup when the trigger is too close to the right edge —
  // otherwise the 256 px popup overflows the viewport and clips Mär/Jun/Sep/Dez.
  const [alignRight, setAlignRight] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const today = new Date()
  const parsedYear  = value ? parseInt(value.slice(0, 4), 10) : today.getFullYear()
  const parsedMonth = value ? parseInt(value.slice(5, 7), 10) : 0

  const [displayYear, setDisplayYear] = useState(parsedYear)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Sync displayYear when external value changes
  useEffect(() => {
    if (value) setDisplayYear(parseInt(value.slice(0, 4), 10))
  }, [value])

  // Decide popup alignment whenever it opens — measured against the actual
  // viewport so we handle window resize, sidebar collapse, etc. correctly.
  useLayoutEffect(() => {
    if (!open || !ref.current) return
    const POPUP_WIDTH = 256 // matches w-64 below
    const SAFE_GUTTER = 16
    const rect = ref.current.getBoundingClientRect()
    const overflow = rect.left + POPUP_WIDTH > window.innerWidth - SAFE_GUTTER
    setAlignRight(overflow)
  }, [open])

  // German numeric format MM.YYYY (e.g. "07.2025"). The popup grid still shows
  // month names for selection; only the collapsed trigger value is numeric.
  const displayLabel = value
    ? `${String(parsedMonth).padStart(2, '0')}.${parsedYear}`
    : placeholder

  const handleSelect = (monthIdx: number) => {
    const mm = String(monthIdx + 1).padStart(2, '0')
    onChange(`${displayYear}-${mm}`)
    setOpen(false)
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange('')
    setOpen(false)
  }

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-expanded={open}
        onClick={() => setOpen(v => !v)}
        className={[
          size === 'lg'
            ? 'h-12 bg-surface-container-lowest border rounded-xl px-4 flex items-center gap-2 transition-all duration-300 outline-none text-base font-bold w-full'
            : 'h-10 bg-surface-container-lowest border rounded-xl px-3 flex items-center gap-2 transition-all duration-300 outline-none text-xs font-bold w-full',
          open
            ? 'border-primary ring-4 ring-primary/5 shadow-lg text-primary'
            : 'border-outline-variant/60 hover:border-primary/30 text-on-surface',
        ].join(' ')}
      >
        <span className="material-symbols-outlined !text-[16px] text-primary" aria-hidden="true">calendar_month</span>
        <span className={value ? 'text-on-surface flex-1 text-left' : 'text-on-surface-variant font-medium flex-1 text-left'}>{displayLabel}</span>
        {value && (
          <span
            role="button"
            aria-label={clearLabel}
            onClick={handleClear}
            className="material-symbols-outlined !text-[14px] text-outline-variant hover:text-error transition-colors ml-1 cursor-pointer"
          >
            close
          </span>
        )}
        <span
          className={`material-symbols-outlined !text-[16px] text-outline-variant transition-transform duration-300 ${open ? 'rotate-180 text-primary' : ''}`}
          aria-hidden="true"
        >
          expand_more
        </span>
      </button>

      {open && (
        <div className={`absolute top-full mt-1.5 w-64 bg-surface-container-lowest border border-outline-variant/40 rounded-2xl shadow-2xl p-4 z-[120] ${alignRight ? 'right-0' : 'left-0'}`}>
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={() => setDisplayYear(y => y - 1)}
              className="p-1 hover:bg-surface-container-high rounded-lg transition-colors"
              aria-label="Vorheriges Jahr"
            >
              <span className="material-symbols-outlined !text-lg text-on-surface-variant">chevron_left</span>
            </button>
            <span className="text-sm font-bold text-on-surface tracking-tight">{displayYear}</span>
            <button
              type="button"
              onClick={() => setDisplayYear(y => y + 1)}
              className="p-1 hover:bg-surface-container-high rounded-lg transition-colors"
              aria-label="Nächstes Jahr"
            >
              <span className="material-symbols-outlined !text-lg text-on-surface-variant">chevron_right</span>
            </button>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {MONTH_NAMES_DE.map((name, idx) => {
              const isSelected = parsedMonth === idx + 1 && parsedYear === displayYear
              const cellN = displayYear * 12 + idx
              const isDisabled = cellN < minN || cellN > maxN
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => !isDisabled && handleSelect(idx)}
                  disabled={isDisabled}
                  className={[
                    'rounded-lg py-1.5 text-[11px] font-bold transition-all',
                    isDisabled
                      ? 'text-on-surface-variant/25 cursor-not-allowed'
                      : isSelected
                        ? 'bg-primary text-on-primary shadow-md shadow-primary/20'
                        : 'hover:bg-primary/5 text-on-surface-variant',
                  ].join(' ')}
                >
                  {name.slice(0, 3)}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
